import { resetTimeout, executeAt } from "."
import moment from "moment"

function makeRange(atDate, day, hour) {
  const fromHour = moment(hour.schedule.from, "HH:mm")
  const fromDate = moment(atDate)
    .day(day.weekday)
    .hour(fromHour.hour())
    .minute(fromHour.minute())
    .second(0)
    .millisecond(0)
  const toHour = moment(hour.schedule.to, "HH:mm")
  const fixMidnight = moment(toHour)
    .endOf("day")
    .isAfter(moment(atDate).endOf("day"))
    ? 1
    : 0
  const toDate = moment(atDate)
    .day(day.weekday + fixMidnight)
    .hour(toHour.hour())
    .minute(toHour.minute() + 1)
    .second(0)
    .millisecond(0)
  if (atDate.isAfter(toDate)) {
    fromDate.day(fromDate.day() + 7)
    toDate.day(toDate.day() + 7)
  }
  return {
    from: fromDate,
    to: toDate
  }
}

function mapToDateRanges(offersScheduling, atDate, schedulingConf) {
  const offersSchedulingRanges = {}
  Object.keys(offersScheduling).forEach(offerId => {
    const offerScheduling = offersScheduling[offerId]
    const days = Object.keys(offerScheduling.days)
      .filter(dayStr => schedulingConf.days[dayStr])
      .map(dayStr => schedulingConf.days[dayStr])
    const hours = Object.keys(offerScheduling.hours)
      .filter(hourStr => schedulingConf.hours[hourStr])
      .map(hourStr => schedulingConf.hours[hourStr])
    const dateRange = []
    days.forEach(day => {
      hours.forEach(hour => {
        dateRange.push(makeRange(atDate, day, hour))
      })
    })
    offersSchedulingRanges[offerId] = dateRange
  })
  return offersSchedulingRanges
}

function findActiveOfferIds(offersScheduling, schedulingConf) {
  const now = moment()
  const alwaysActiveOfferIds = Object.keys(offersScheduling).filter(
    offerId => offersScheduling[offerId].alwaysActive
  )
  const offersSchedulingRanges = mapToDateRanges(
    offersScheduling,
    now,
    schedulingConf
  )
  const activeOfferIdsByScheduling = Object.keys(offersSchedulingRanges)
    .filter(
      offerId =>
        !!offersSchedulingRanges[offerId].find(
          ({ from, to }) => now.isAfter(from) && now.isBefore(to)
        )
    )
    .filter(offerId => !offersScheduling[offerId].alwaysActive)
  return [...alwaysActiveOfferIds, ...activeOfferIdsByScheduling]
}

function computeNextCheck(offersScheduling, schedulingConf) {
  const now = moment()
  const offersSchedulingRanges = mapToDateRanges(
    offersScheduling,
    now,
    schedulingConf
  )
  const activeOrNext = Object.values(offersSchedulingRanges)
    .reduce((acc, r) => acc.concat(r), []) // flatMap
    .sort((r1, r2) => r1.from.unix() - r2.from.unix())
    .find(
      ({ from, to }) =>
        (now.isAfter(from) && now.isBefore(to)) || now.isBefore(from)
    )
  const result = activeOrNext
    ? now.isAfter(activeOrNext.from)
      ? activeOrNext.to
      : activeOrNext.from
    : undefined
  if (result) {
    return result.valueOf()
  }
}

export default {
  schedule(scheduling, schedulingConf, callback, errorCallback) {
    try {
      callback(findActiveOfferIds(scheduling, schedulingConf))
      const nextCheck = computeNextCheck(scheduling, schedulingConf)
      if (nextCheck) {
        executeAt(nextCheck, () => this.schedule(...arguments), "switch offer")
      }
    } catch (e) {
      errorCallback(e)
      resetTimeout("retry", () => this.schedule(...arguments), 30 * 1000)
    }
  },
  getRangesOverlappingOffers(offersScheduling, schedulingConf) {
    const offersSchedulingRanges = mapToDateRanges(
      offersScheduling,
      moment(),
      schedulingConf
    )
    const rangesOverlappingOffers = {}
    const offerIds = Object.keys(offersSchedulingRanges)
    offerIds.forEach(offerId => {
      offersSchedulingRanges[offerId].forEach(range => {
        const overlappingIds = []
        offerIds
          .filter(id => id !== offerId)
          .forEach(otherOfferId => {
            const overlap = offersSchedulingRanges[otherOfferId].some(
              otherRange =>
                otherRange.from.isBetween(range.from, range.to) ||
                otherRange.to.isBetween(range.from, range.to) ||
                (otherRange.from.isSameOrBefore(range.from) &&
                  otherRange.to.isSameOrAfter(range.to))
            )
            if (overlap) {
              overlappingIds.push(otherOfferId)
              return
            }
          })
        if (overlappingIds.length > 0) {
          const anotherIds = overlappingIds.filter(
            id =>
              !rangesOverlappingOffers[offerId] ||
              !rangesOverlappingOffers[offerId].includes(id)
          )
          rangesOverlappingOffers[offerId] = rangesOverlappingOffers[offerId]
            ? [...rangesOverlappingOffers[offerId], ...anotherIds]
            : anotherIds
        }
      })
    })
    return rangesOverlappingOffers
  }
}
